import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  Loader2,
  MapPin,
  MessageSquare,
  ShieldAlert,
  Target,
  User,
  X,
  Building2,
  Clock,
} from "lucide-react";
import { apiFetch } from "../../api/client";
import logoAtletain from "../../utils/Logo_Atletain.png";

const InfoLine = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon size={16} className="mt-0.5 flex-shrink-0 text-blue-950" />
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
};

const GuardianReviewRoute = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    apiFetch(`/guardian/review?token=${encodeURIComponent(token)}`, { auth: false })
      .then((res) => {
        if (res.valid) {
          setData(res);
          setState("valid");
        } else if (res.expired) {
          setState("expired");
        } else if (res.already_decided) {
          setResult(res.decision);
          setState("decided");
        } else {
          setState("invalid");
        }
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const handleDecision = async (decision) => {
    if (deciding) return;
    setDeciding(true);
    try {
      const res = await apiFetch("/guardian/review", {
        method: "POST",
        body: { token, decision },
        auth: false,
      });
      if (res.success) {
        setResult(decision);
        setState("decided");
      } else if (res.already_decided) {
        setResult(res.decision);
        setState("decided");
      }
    } catch (err) {
      if (err?.code === "token_expired") {
        setState("expired");
      } else if (err?.code === "invalid_token") {
        setState("invalid");
      } else {
        setState("error");
      }
    } finally {
      setDeciding(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex justify-center">
          <img src={logoAtletain} alt="Athletain" className="h-12 w-12 object-contain" />
        </div>

        {state === "loading" && (
          <div className="flex flex-col items-center py-16">
            <Loader2 size={32} className="animate-spin text-blue-500" />
            <p className="mt-3 text-sm text-gray-500">Cargando solicitud...</p>
          </div>
        )}

        {state === "invalid" && (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <ShieldAlert size={40} className="mx-auto mb-3 text-gray-400" />
            <h2 className="text-lg font-bold text-gray-900">Enlace inválido</h2>
            <p className="mt-2 text-sm text-gray-500">
              Este enlace no es válido o ya fue utilizado.
            </p>
          </div>
        )}

        {state === "expired" && (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <Clock size={40} className="mx-auto mb-3 text-amber-500" />
            <h2 className="text-lg font-bold text-gray-900">Enlace expirado</h2>
            <p className="mt-2 text-sm text-gray-500">
              Este enlace ha expirado. El scout puede enviar una nueva solicitud.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <ShieldAlert size={40} className="mx-auto mb-3 text-red-400" />
            <h2 className="text-lg font-bold text-gray-900">Error</h2>
            <p className="mt-2 text-sm text-gray-500">
              Ocurrió un error al procesar tu decisión.
            </p>
            <button
              type="button"
              onClick={() => setState("valid")}
              className="mt-4 rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Intentar de nuevo
            </button>
          </div>
        )}

        {state === "decided" && (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            {result === "approved" ? (
              <>
                <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-green-100">
                  <Check size={24} className="text-green-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Solicitud aprobada</h2>
                <p className="mt-2 text-sm text-gray-500">
                  El scout ya puede ver el perfil completo del atleta y escribirle por
                  mensajes en Athletain. Le avisamos al atleta y al scout.
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-red-100">
                  <X size={24} className="text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Solicitud rechazada</h2>
                <p className="mt-2 text-sm text-gray-500">
                  La solicitud ha sido rechazada. El atleta y el scout han sido notificados.
                </p>
              </>
            )}
          </div>
        )}

        {state === "valid" && data && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900">
                Solicitud de contacto
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Un scout quiere contactar a <strong>{data.athlete_name}</strong> en
                Athletain, una plataforma donde deportistas registran su progreso y
                scouts y academias buscan talento. Como es menor de edad y te
                indicó como su tutor legal, el scout necesita tu aprobación.
              </p>
            </div>

            <div className="border-b border-gray-200 p-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  {data.scout.full_name}
                </h3>
                {data.scout.organization_name && (
                  <p className="text-sm text-gray-500">{data.scout.organization_name}</p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoLine icon={Target} label="Oportunidad" value={data.request.opportunity_type} />
                <InfoLine icon={MapPin} label="Ubicación" value={data.request.location} />
                <InfoLine icon={User} label="Motivo" value={data.request.attention_reason} />
                <InfoLine icon={Building2} label="Busca" value={data.request.looking_for} />
              </div>

              {data.request.message && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-gray-50 p-3">
                  <MessageSquare size={16} className="mt-0.5 flex-shrink-0 text-blue-950" />
                  <p className="text-sm italic text-gray-700">{data.request.message}</p>
                </div>
              )}
            </div>

            <div className="space-y-2 border-b border-gray-200 p-6 text-sm text-gray-600">
              <p>
                <strong className="text-gray-900">Si apruebas:</strong> el scout podrá
                ver el perfil completo de {data.athlete_name}, incluidos su correo y
                teléfono, y escribirle por mensajes dentro de Athletain.
              </p>
              <p>
                <strong className="text-gray-900">Si rechazas:</strong> el scout no
                podrá contactarle. Podría enviar una nueva solicitud más adelante, que
                también te llegaría a ti.
              </p>
              <p className="text-xs text-gray-500">
                ¿No conoces a {data.athlete_name} o no reconoces esta solicitud?
                Recházala y escríbenos a{" "}
                <a href="mailto:soporte@athletain.com" className="font-medium text-blue-900 underline">
                  soporte@athletain.com
                </a>
                .
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 p-6">
              <button
                type="button"
                onClick={() => handleDecision("approved")}
                disabled={deciding}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deciding ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                Aprobar
              </button>
              <button
                type="button"
                onClick={() => handleDecision("rejected")}
                disabled={deciding}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X size={16} />
                Rechazar
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-gray-400">
          Athletain — Plataforma de talento deportivo
        </p>
      </div>
    </div>
  );
};

export default GuardianReviewRoute;
